import { useCallback, useMemo } from 'react';
import {
  DEAL_STAGE_KEY,
  ENGAGEMENT_MANAGER_KEY,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  STATUS_KEY,
} from 'common/constants/analyst-dashboards';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkboardSpreadsheetView from 'dashboard409a/components/WorkboardSpreadsheetView';
import useWorkboardSheet from 'dashboard409a/hooks/useWorkboardSheet';
import getWorkboardClickedRecordId from 'dashboard409a/utilities/getWorkboardClickedRecordId';
import { WorkboardSpreadsheetProps } from './WorkboardSpreadsheet.props';

const INTERACTIVE_COLUMN_IDS = new Set([
  STATUS_KEY,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  ENGAGEMENT_MANAGER_KEY,
  DEAL_STAGE_KEY,
]);

const WorkboardSpreadsheet = ({
  valuations,
  visibleColumnIds,
  isLoading = false,
  columnWidths,
  statusOptions,
  statusOptionsLoading,
  isNoteLoading = false,
  dealStageOptions,
  sortCriteria,
  dealGroups,
  collapsedDealIds,
  onToggleCollapsedDeal,
  onRequestSort,
  onColumnReorder,
  onColumnResize,
  onRowClick,
  onStatusUpdate,
  onNoteSave,
  onEngagementManagerUpdate,
  onOtherConsultantsUpdate,
  onDealStageChange,
}: WorkboardSpreadsheetProps) => {
  const valuationById = useMemo(
    () => new Map(valuations.map(valuation => [valuation.record_id, valuation])),
    [valuations]
  );

  const handleSheetClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const recordId = getWorkboardClickedRecordId(event, {
        interactiveColumnIds: INTERACTIVE_COLUMN_IDS,
        ignoreCellClassNames: ['workboard-deal-group-cell'],
      });
      if (!recordId) {
        return;
      }

      const valuation = valuationById.get(recordId);
      if (!valuation) {
        return;
      }

      onRowClick(valuation, event);
    },
    [onRowClick, valuationById]
  );

  const { spreadsheet } = useWorkboardSheet({
    valuations,
    visibleColumnIds,
    statusOptions,
    statusOptionsLoading,
    isNoteLoading,
    dealStageOptions,
    sortCriteria,
    dealGroups,
    collapsedDealIds,
    onToggleCollapsedDeal,
    onRequestSort,
    onStatusUpdate,
    onNoteSave,
    onEngagementManagerUpdate,
    onOtherConsultantsUpdate,
    onDealStageChange,
  });

  const spreadsheets = useMemo(() => [spreadsheet], [spreadsheet]);

  const { cells, data, onChange, workbook } = useWorkbook(spreadsheets);

  const sheet = data?.workboard;
  const isReady = !isLoading && Boolean(data && cells && sheet);

  return (
    <WorkboardSpreadsheetView
      visibleColumnIds={visibleColumnIds}
      columnWidths={columnWidths}
      isReady={isReady}
      sheet={sheet}
      cells={cells?.workboard}
      workbook={workbook}
      onChange={onChange}
      onColumnReorder={onColumnReorder}
      onColumnResize={onColumnResize}
      onSheetClick={handleSheetClick}
      onRequestSort={onRequestSort}
      extraContainerSx={{
        '& .data-grid td.workboard-deal-group-cell': {
          backgroundColor: 'rgba(0,0,0,0.025)',
          borderBottom: '1px solid rgba(0,0,0,0.07)',
          verticalAlign: 'middle',
        },
      }}
    />
  );
};

export default WorkboardSpreadsheet;
